import { useState, useEffect } from 'react';
import JWModal from '../brand/JWModal';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import axiosService from '../../utils/axios';
import { useSelector } from 'react-redux';
import { EventType } from '../../types/calendar';
import { HighlightType } from '../../types/profile';
import { CalendarIcon, LinkIcon, Type, AlignLeft } from 'lucide-react';
import './modal-buttons.css';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { eventDocumentTitle } from '@/utils/documentTitle';

interface AddEventModalProps {
    event?: EventType
    show: boolean
    setShow: (show: boolean) => void
    highlight?: HighlightType | undefined | null
    onClose: () => void
    fetchEvents: () => void
    setSelectedEvent: (event: EventType | undefined) => void
}

function EventModal(props: AddEventModalProps) {
    const profile = useSelector((state: any) => state.auth.profile);
    const user = useSelector((state: any) => state.auth.user);
    const [eventTitle, setEventTitle] = useState<string | undefined | null>(null);
    const [eventDescription, setEventDescription] = useState<string | undefined | null>(null);
    const [eventDate, setEventDate] = useState<Date | null>(null);
    const [eventLink, setEventLink] = useState<string | undefined | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useDocumentTitle(props.show ? eventDocumentTitle(eventTitle ?? props.event?.title) : null);

    const formatLink = (link: string): string => {
        if (!link) return '';
        if (link.startsWith('http://') || link.startsWith('https://')) return link;
        return `https://${link}`;
    };

    useEffect(() => {
        if (props?.event) {
            setEventTitle(props.event?.title || '');
            setEventDescription(props.event?.description || '');
            setEventDate(props.event?.dateTime ? new Date(props.event.dateTime) : new Date());
            setEventLink(props.event?.link ? formatLink(props.event.link) : '');
        } else {
            // Set default values for new event
            setEventDate(new Date());
            setEventTitle('');
            setEventDescription('');
            setEventLink('');
        }
    }, [props?.event]);

    const addEvent = async () => {
        if (!eventTitle || !eventDate) return;

        setIsSubmitting(true);
        try {
            let newEvent = null;
            const formattedLink = eventLink ? formatLink(eventLink) : '';

            // Get the profile ID - handle different possible structures
            const profileId = profile?._id || user?.profileId;

            if (!profileId) {
                console.error('No profile ID available for event creation');
                alert('Unable to create event: Profile not found. Please try refreshing the page.');
                setIsSubmitting(false);
                return;
            }

            // Ensure the date is valid
            if (isNaN(eventDate.getTime())) {
                alert('Invalid date selected. Please choose a valid date and time.');
                setIsSubmitting(false);
                return;
            }

            if (!props?.event?._id) {
                const eventRes = await axiosService.post(
                    '/events',
                    {
                        title: eventTitle,
                        description: eventDescription || '',
                        dateTime: eventDate.toISOString(),
                        link: formattedLink,
                    },
                    {
                        params: {
                            profileId: profileId,
                        },
                    }
                );
                newEvent = eventRes?.data;
            } else {
                const eventRes = await axiosService.put(`/events/${props.event._id}`, {
                    title: eventTitle,
                    description: eventDescription || '',
                    dateTime: eventDate.toISOString(),
                    link: formattedLink,
                });
                newEvent = eventRes?.data;
            }
            await props.fetchEvents();
            if (newEvent) {
                props.setSelectedEvent(newEvent);
            }
            props.onClose();
        } catch (error: any) {
            console.error('Error saving event:', error);

            // Provide more specific error messages
            let errorMessage = 'Failed to save event. Please try again.';

            if (error.response?.status === 401) {
                errorMessage = 'You are not authorized to create events. Please log in again.';
            } else if (error.response?.status === 404) {
                errorMessage = 'Profile not found. Please refresh the page and try again.';
            } else if (error.response?.status === 400) {
                errorMessage = error.response?.data?.message || 'Invalid event data. Please check your inputs.';
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            alert(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid = eventTitle && eventDate;

    return (
        <JWModal
            show={props.show}
            onClose={props.onClose}
            setShow={props.setShow}
            wrapperClassName={'max-w-[500px] w-[90%] min-h-[598px] bg-white rounded-lg'}
        >
            <div className="flex flex-col h-full">
                <div className="flex flex-col flex-grow gap-4 px-6 pt-4 pb-6">
                    {/* Title Section */}
                    <div className="space-y-1.5">
                        <Label
                            for="eventTitle"
                            className="text-sm font-medium text-gray-700 flex items-center gap-2"
                        >
                            <Type size={14} className="text-gray-500" />
                            Event Title*
                        </Label>
                        <Input
                            id="eventTitle"
                            name="title"
                            placeholder="Enter event title"
                            value={eventTitle || ''}
                            onChange={(e) => setEventTitle(e.target.value)}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm"
                        />
                    </div>

                    {/* Date and Time Section */}
                    <div className="space-y-1.5">
                        <Label
                            for="eventDate"
                            className="text-sm font-medium text-gray-700 flex items-center gap-2"
                        >
                            <CalendarIcon size={14} className="text-gray-500" />
                            Date and Time*
                        </Label>
                        <Input
                            id="eventDate"
                            name="date"
                            type="datetime-local"
                            value={
                                eventDate
                                    ? new Date(
                                        eventDate.getTime() -
                                        eventDate.getTimezoneOffset() * 60000
                                    )
                                        .toISOString()
                                        .slice(0, -8)
                                    : ''
                            }
                            onChange={(e) => setEventDate(new Date(e.target.value))}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm"
                        />
                    </div>

                    {/* Description Section */}
                    <div className="space-y-1.5">
                        <Label
                            for="eventDescription"
                            className="text-sm font-medium text-gray-700 flex items-center gap-2"
                        >
                            <AlignLeft size={14} className="text-gray-500" />
                            Description (Optional)
                        </Label>
                        <Input
                            id="eventDescription"
                            name="description"
                            type="textarea"
                            placeholder="Enter event description"
                            value={eventDescription || ''}
                            onChange={(e) => setEventDescription(e.target.value)}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm h-[70px]"
                        />
                    </div>

                    {/* Event Link Section */}
                    <div className="space-y-1.5">
                        <Label
                            for="eventLink"
                            className="text-sm font-medium text-gray-700 flex items-center gap-2"
                        >
                            <LinkIcon size={14} className="text-gray-500" />
                            Event Link (Optional)
                        </Label>
                        <Input
                            id="eventLink"
                            name="link"
                            placeholder="https://example.com"
                            value={eventLink || ''}
                            onChange={(e) => setEventLink(e.target.value)}
                            onBlur={(e) => setEventLink(formatLink(e.target.value))}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm"
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 px-6 pb-8">
                    <Button
                        outline
                        onClick={props.onClose}
                        className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={addEvent}
                        disabled={!isFormValid || isSubmitting}
                        className={`px-4 py-1.5 text-sm font-medium text-white rounded-md ${
                            isFormValid && !isSubmitting
                                ? 'bg-blue-600 hover:bg-blue-700'
                                : 'bg-blue-400 cursor-not-allowed'
                        }`}
                    >
                        {isSubmitting
                            ? 'Saving...'
                            : props.event
                                ? 'Update Event'
                                : 'Create Event'
                        }
                    </Button>
                </div>
            </div>
        </JWModal>
    );
}

export default EventModal;
